import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { PATHS, pathsFor, readSlugs, markRead, nextUnread, PROGRESS_EVENT, NEWSLETTER_KEY } from "@/lib/startHere";
import NewsletterCapture from "@/components/NewsletterCapture";
import PathShareCard from "@/components/PathShareCard";

function usePathProgress() {
  const [read, setRead] = useState(readSlugs);
  useEffect(() => {
    const sync = () => setRead(readSlugs());
    window.addEventListener(PROGRESS_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(PROGRESS_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  return read;
}

function PathStrip({ path, read }) {
  const next = nextUnread(read, path.essays);
  const done = path.essays.filter((e) => read.has(e.slug)).length;
  const id = path.id === "start-here" ? "start-here" : `path-${path.id}`;
  return (
    <div className="border border-terracotta/40 bg-obsidian-800/40 p-6 sm:p-8" data-testid={`${id}-strip`}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-2">
            {path.name}{done ? ` · ${done} of ${path.essays.length} read` : ""}
          </p>
          <p className="font-serif text-xl sm:text-2xl text-bone-100">{next ? path.lead : "You've read the whole path."}</p>
        </div>
        {done > 0 && next && (
          <Link to={`/blog/${next.slug}`} data-testid={`${id}-resume`} className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-5 min-h-[44px] font-sans text-xs uppercase tracking-[0.22em] transition-colors">
            Pick up at {next.number} <ArrowRight className="h-4 w-4" />
          </Link>
        )}
      </div>
      <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 list-none p-0">
        {path.essays.map((s, i) => {
          const isRead = read.has(s.slug);
          return (
            <li key={s.slug}>
              <Link to={`/blog/${s.slug}`} data-testid={`${id}-step-${i + 1}`} data-read={isRead ? "true" : "false"} className="group flex gap-3 items-baseline">
                <span className="font-sans text-xs text-terracotta w-5 shrink-0">
                  {isRead ? <Check className="h-3.5 w-3.5 inline" aria-label="Read" data-testid={`${id}-check-${i + 1}`} /> : i + 1}
                </span>
                <span className={`font-serif leading-snug transition-colors group-hover:text-terracotta ${isRead ? "text-bone-500" : "text-bone-200"}`}>{s.title}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function StartHereStrip() {
  const read = usePathProgress();
  return (
    <section className="max-w-6xl mx-auto px-6 md:px-12 pb-12 space-y-6" aria-label="Reading paths">
      {PATHS.map((p) => <PathStrip key={p.id} path={p} read={read} />)}
    </section>
  );
}

// Marks a path essay as read once the reader scrolls to the end of what they can see.
export function ReadSentinel({ slug }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!pathsFor(slug).length) return undefined;
    // Requires a real scroll: a short logged-out preview can fit on screen at load.
    const onScroll = () => {
      const el = ref.current;
      if (!el || window.scrollY < 40) return;
      if (el.getBoundingClientRect().top < window.innerHeight) {
        markRead(slug);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);
  return <div ref={ref} aria-hidden="true" data-testid="read-sentinel" className="h-px" />;
}

export function PathResumeBanner({ slug }) {
  const read = usePathProgress();
  const active = PATHS.map((p) => ({ p, next: nextUnread(read, p.essays), done: p.essays.filter((e) => read.has(e.slug)).length }))
    .find((x) => x.done > 0 && x.next);
  if (!active || active.next.slug === slug) return null;
  return (
    <Link to={`/blog/${active.next.slug}`} data-testid="path-resume-banner" className="mb-8 flex items-center justify-between gap-4 border border-terracotta/40 bg-obsidian-800/50 px-5 py-3 group">
      <span className="font-sans text-xs uppercase tracking-[0.2em] text-bone-400">
        {active.p.name} · {active.done} of {active.p.essays.length} read
      </span>
      <span className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta group-hover:text-bone-100 inline-flex items-center gap-2">
        Pick up at {active.next.number} <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}

function isSubscribed() {
  try {
    return window.localStorage.getItem(NEWSLETTER_KEY) === "1";
  } catch {
    return false;
  }
}

function PathFinish({ path, read }) {
  const next = nextUnread(read, path.essays);
  if (next) {
    return (
      <div>
        <p className="font-serif text-xl text-bone-100 mb-3">That's the last essay in the path.</p>
        <Link to={`/blog/${next.slug}`} data-testid="next-in-path-skipped" className="font-sans text-xs uppercase tracking-[0.22em] text-terracotta hover:text-bone-100">
          You skipped one — pick up at {next.number} →
        </Link>
      </div>
    );
  }
  return (
    <div data-testid="path-finished">
      <p className="font-serif text-2xl text-bone-100 mb-4">You finished the path.</p>
      <PathShareCard path={path} />
      {path.id === "start-here" && !isSubscribed() ? (
        <div data-testid="path-finish-invite">
          <p className="font-sans text-sm text-bone-400 leading-relaxed">Get one new field note each fortnight by email. No noise, one-click unsubscribe.</p>
          <NewsletterCapture source="start-here-finish" />
        </div>
      ) : (
        <Link to="/try" data-testid="next-in-path-try" className="font-sans text-xs uppercase tracking-[0.22em] text-terracotta hover:text-bone-100">
          Try Chapter One — no account →
        </Link>
      )}
    </div>
  );
}

export function NextInPath({ slug }) {
  const read = usePathProgress();
  return pathsFor(slug).map(({ path, index }) => {
    const next = path.essays[index + 1];
    return (
      <aside key={path.id} className="mt-14 border border-terracotta/40 bg-obsidian-800/40 p-6" data-testid={path.id === "start-here" ? "next-in-path" : `next-in-path-${path.id}`}>
        <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-terracotta mb-3">
          {path.name} · {index + 1} of {path.essays.length}
        </p>
        {next ? (
          <Link to={`/blog/${next.slug}`} data-testid={path.id === "start-here" ? "next-in-path-link" : `next-in-path-link-${path.id}`} className="group flex items-center justify-between gap-4">
            <span>
              <span className="block font-sans text-xs uppercase tracking-[0.2em] text-bone-500 mb-1">Next in the path · {index + 2}</span>
              <span className="font-serif text-xl text-bone-100 group-hover:text-terracotta transition-colors">{next.title}</span>
            </span>
            <ArrowRight className="h-5 w-5 text-terracotta shrink-0" />
          </Link>
        ) : (
          <PathFinish path={path} read={read} />
        )}
      </aside>
    );
  });
}
